import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../../assets/css/Header.css';
import samyamaLogo from "../../assets/images/Header.png";

const Header = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Menu data structure with categories
  const menuData = {
    products: {
      business: {
        name: "Business Intelligence Tools",
        items: [
          { name: 'BI Migrator', path: '/products/bimigrator'},
          { name: 'DevOps Buddy', path: '/products/devops-buddy'}
        ]
      },
      healthcare: {
        name: "Healthcare & Clinical Trials",
        items: [
          { name: 'CSDLC', path: '/products/csdlc' },
          { name: 'CSDLC AI', path: '/products/csdlc-ai' },
          { name: 'HCDLC AI', path: '/products/hcdlc-ai' },
          { name: 'Rapid MediCapture EDC', path: '/products/rapid-medi-capture' }
        ]
      },
      educational: {
        name: "Educational & Informational",
        items: [
          { name: 'AISDLC', path: '/products/aisdlc' }
        ]
      }
    }
  };

  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate breadcrumb path from current location
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    
    if (pathSegments.length === 0) {
      return [];
    }
    
    const breadcrumbs = [{ name: 'Home', path: '/' }];
    let currentPath = '';
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // If we're in products section
      if (segment === 'products') {
        breadcrumbs.push({ name: 'Products', path: currentPath });
        
        // If there's a product after 'products'
        if (pathSegments[index + 1]) {
          const productId = pathSegments[index + 1];
          // Find the product in any category
          let productFound = false;
          for (const category of Object.values(menuData.products)) {
            const product = category.items.find(item => item.path.split('/').pop() === productId);
            if (product) {
              // Skip adding the category name, just add the product directly
              breadcrumbs.push({ 
                name: product.name, 
                path: `${currentPath}/${productId}` 
              });
              productFound = true;
              break;
            }
          }
          if (!productFound) {
            // If product not found in any category (shouldn't happen with current structure)
            const formattedName = productId
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            breadcrumbs.push({ name: formattedName, path: `${currentPath}/${productId}` });
          }
        }
      } else if (segment !== pathSegments[pathSegments.length - 1] || !pathSegments.includes('products')) {
        // For non-product pages or intermediate paths
        const formattedName = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        breadcrumbs.push({ name: formattedName, path: currentPath });
      }
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <Link to="/">
              <img src={samyamaLogo} alt="Samyama Logo" className="logo-image" />
            </Link>
          </div>
          
          {/* Mobile menu toggle */}
          {isMobile && (
            <button 
              className="mobile-menu-toggle" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          )}
          
          {/* Navigation - desktop or mobile when open */}
          <nav className={`main-nav ${isMobile ? (mobileMenuOpen ? 'mobile-open' : 'mobile-closed') : ''}`}>
            <ul className="nav-list">
              {location.pathname !== '/' && (
                <li className="nav-item">
                  <Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link>
                </li>
              )}
              <li className="nav-item">
                <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link>
              </li>
              <li className="nav-item dropdown">
                <Link to="/products" className={location.pathname.includes('/products') ? 'active' : ''}>Products</Link>
                <div className="dropdown-content">
                  {Object.entries(menuData.products).map(([key, category]) => (
                    <div key={key} className="dropdown-section">
                      <h3>{category.name}</h3>
                      <ul>
                        {category.items.map((item, index) => (
                          <li key={index}>
                            <Link 
                              to={item.path} 
                              className={location.pathname === item.path ? 'active' : ''}
                            >
                              {item.name}
                              {item.domain && <span className="domain">({item.domain})</span>}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </li>
              <li className="nav-item">
                <Link to="/careers" className={location.pathname === '/careers' ? 'active' : ''}>Careers</Link>
              </li>
              <li className="nav-item">
                <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Breadcrumbs navigation - Only show if not on home page */}
        {location.pathname !== '/' && breadcrumbs.length > 0 && (
          <div className="breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="breadcrumb-separator">&gt;</span>}
                <Link 
                  to={crumb.path} 
                  className={index === breadcrumbs.length - 1 ? 'current' : ''}
                >
                  {crumb.name}
                </Link>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;